import {interstitial} from 'screens/InsomniaSurvey/utils/interstitial';
import {inputPage, radioPage} from 'screens/InsomniaSurvey/utils/page';
import {randomize} from 'screens/InsomniaSurvey/utils/randomize';
import {wholeNumberMask} from 'screens/InsomniaSurvey/utils/inputMasks';
import {RiseSurveyConfig} from 'types';
import {getInsuranceOptionsForState, nameMask} from './utils';
import {MEDICARE_PLAN_TYPES} from './config';
import ApneaScoringInterstitial from 'screens/InsomniaSurvey/interstitials/ApneaRiskInterstitial/ApneaScoringInterstitial';
import ISISleepQualityInterstitial from 'screens/InsomniaSurvey/interstitials/ISISnapshotInterstitial/ISISleepQualityInterstitial';

const YES_NO_CHOICES = ['Yes', 'No'];

/**
 * The onboarding half of the clinic intake — everything from `age` through
 * `insurance_coverage`, identical for every downstream care path.
 *
 * Shared by the SleepioRx funnel (`insomnia_funnel`) and the appointment-booking
 * funnel (`insomnia_booking`) so the question set can't drift between them. The
 * flows diverge at `insurance_state`, whose `onNext` decides eligibility — each
 * config declares that page itself and appends its own tail.
 *
 * Note: `randomize()` runs at module load, so both configs share one shuffle per
 * page load. That matches the previous single-config behavior.
 */
export const onboardingPages: RiseSurveyConfig['pages'] = [
  radioPage({
    category: 'About Me',
    name: 'age',
    title: 'How old are you?',
    description: 'Sleep patterns change with age',
    choices: ['18-23', '24-29', '30s', '40s', '50s', '60+'],
    autoAdvance: true,
    isRequired: true,
    identifyPropertyName: 'age',
  }),

  radioPage({
    category: 'About Me',
    name: 'gender',
    title: 'What gender do you identify with?',
    description: 'Both biological and societal factors influence sleep patterns',
    choices: ['Female', 'Male'],
    autoAdvance: true,
    isRequired: true,
    identifyPropertyName: 'gender',
  }),

  radioPage({
    category: 'About Me',
    name: 'goal',
    title: 'What is your primary goal?',
    description: 'We’ll personalize your plan to match your goal',
    choices: randomize([
      'Fall asleep faster',
      'Stay asleep through the night',
      'Improve sleep quality',
      'Wake up feeling refreshed',
      'Feel more energized during the day',
    ]),
    autoAdvance: true,
    isRequired: true,
    identifyPropertyName: 'primary_goal',
  }),

  radioPage({
    category: 'Sleep Quality',
    name: 'insomnia_falling_asleep',
    title: 'How difficult is it for you to FALL asleep?',
    description: 'Reflect on the past two weeks',
    choices: [
      {value: 0, text: 'Not at all difficult'},
      {value: 1, text: 'A little difficult'},
      {value: 2, text: 'Somewhat difficult'},
      {value: 3, text: 'Difficult'},
      {value: 4, text: 'Very difficult'},
    ],
    autoAdvance: true,
    isRequired: true,
  }),

  radioPage({
    category: 'Sleep Quality',
    name: 'insomnia_staying_asleep',
    title: 'How difficult is it for you to STAY asleep?',
    description: 'Reflect on the past two weeks',
    choices: [
      {value: 0, text: 'Not at all difficult'},
      {value: 1, text: 'A little difficult'},
      {value: 2, text: 'Somewhat difficult'},
      {value: 3, text: 'Difficult'},
      {value: 4, text: 'Very difficult'},
    ],
    autoAdvance: true,
    isRequired: true,
  }),

  radioPage({
    category: 'Sleep Quality',
    name: 'insomnia_waking_up',
    title: 'Rate your problem with waking up too early',
    description: 'Reflect on the past two weeks',
    choices: [
      {value: 0, text: 'Not an issue'},
      {value: 1, text: 'Mild'},
      {value: 2, text: 'Moderate'},
      {value: 3, text: 'Severe'},
      {value: 4, text: 'Very severe'},
    ],
    autoAdvance: true,
    isRequired: true,
  }),

  radioPage({
    category: 'Sleep Quality',
    name: 'insomnia_daily_functioning',
    title: 'Does your sleep interfere with your daily functioning?',
    description: 'Consider your daytime fatigue, ability to focus, and mood',
    choices: [
      {value: 0, text: 'Not at all'},
      {value: 1, text: 'Barely'},
      {value: 2, text: 'Somewhat'},
      {value: 3, text: 'Much'},
      {value: 4, text: 'Very much interfering'},
    ],
    autoAdvance: true,
    isRequired: true,
  }),

  radioPage({
    category: 'Sleep Quality',
    name: 'insomnia_sleep_pattern',
    title: 'How satisfied are you with your sleep pattern?',
    description: 'Reflect on the past two weeks',
    choices: [
      {value: 0, text: 'Very satisfied'},
      {value: 1, text: 'Satisfied'},
      {value: 2, text: 'Moderately satisfied'},
      {value: 3, text: 'Dissatisfied'},
      {value: 4, text: 'Very dissatisfied'},
    ],
    autoAdvance: true,
    isRequired: true,
  }),

  radioPage({
    category: 'Sleep Quality',
    name: 'insomnia_noticeable_to_others',
    title: 'How noticeable do you think your sleep problems are to others?',
    description: 'This helps us understand your sleep quality',
    choices: [
      {value: 0, text: 'Not at all noticeable'},
      {value: 1, text: 'A little'},
      {value: 2, text: 'Somewhat'},
      {value: 3, text: 'Much'},
      {value: 4, text: 'Very much noticeable'},
    ],
    autoAdvance: true,
    isRequired: true,
  }),

  radioPage({
    category: 'Sleep Quality',
    name: 'insomnia_worried',
    title: 'How worried/distressed are you about your sleep?',
    description: 'This helps us understand your sleep quality',
    choices: [
      {value: 0, text: 'Not at all worried'},
      {value: 1, text: 'A little'},
      {value: 2, text: 'Somewhat'},
      {value: 3, text: 'Much'},
      {value: 4, text: 'Very much worried'},
    ],
    autoAdvance: true,
    isRequired: true,
  }),

  // Name collection page with First Name and Last Name
  inputPage({
    name: 'name_collection',
    category: 'Sleep Profile',
    title: 'What is your name?',
    description: "We'll use this to personalize your sleep profile",
    inputs: [
      {
        name: 'first_name',
        placeholder: 'Enter first name',
        isRequired: true,
        inputMask: nameMask,
      },
      {
        name: 'last_name',
        placeholder: 'Enter last name',
        isRequired: true,
        inputMask: nameMask,
      },
    ],
  }),

  interstitial({
    name: 'sleep_quality',
    category: 'Sleep Profile',
    component: () => <ISISleepQualityInterstitial />,
  }),

  radioPage({
    name: 'apnea_snore',
    category: 'Sleep Apnea',
    title: 'Have you been told you snore?',
    choices: YES_NO_CHOICES,
    autoAdvance: true,
    isRequired: true,
  }),

  radioPage({
    name: 'apnea_dry_mouth',
    category: 'Sleep Apnea',
    title: 'Do you sometimes wake up with a dry mouth?',
    choices: YES_NO_CHOICES,
    autoAdvance: true,
    isRequired: true,
  }),

  radioPage({
    name: 'apnea_morning_headaches',
    category: 'Sleep Apnea',
    title: 'Do you ever wake up with headaches in the morning?',
    choices: YES_NO_CHOICES,
    autoAdvance: true,
    isRequired: true,
  }),

  radioPage({
    name: 'apnea_daytime_sleepy',
    category: 'Sleep Apnea',
    title: 'Even after sleeping through the night, do you feel sleepy during the day?',
    choices: YES_NO_CHOICES,
    autoAdvance: true,
    isRequired: true,
  }),

  radioPage({
    name: 'apnea_stop_breathing',
    category: 'Sleep Apnea',
    title: 'Has anyone ever noticed you hold your breath during sleep?',
    choices: YES_NO_CHOICES,
    autoAdvance: true,
    isRequired: true,
  }),

  radioPage({
    name: 'apnea_sleep_pain_frequency',
    category: 'Sleep Apnea',
    title: 'How often have you had trouble sleeping because of pain?',
    choices: ['Never', 'Less than once a week', 'Once or twice a week', 'Three or more times a week'],
    autoAdvance: true,
    isRequired: true,
  }),

  radioPage({
    name: 'apnea_gasping',
    category: 'Sleep Apnea',
    title: 'Do you ever wake gasping for breath?',
    choices: YES_NO_CHOICES,
    autoAdvance: true,
    isRequired: true,
  }),

  radioPage({
    name: 'apnea_high_blood_pressure',
    category: 'Sleep Apnea',
    title: 'Do you have (or are you being treated for) high blood pressure?',
    choices: YES_NO_CHOICES,
    autoAdvance: true,
    isRequired: true,
  }),

  inputPage({
    name: 'height',
    category: 'Sleep Apnea',
    title: 'What is your height?',
    description: 'This helps us personalize your sleep recommendations.',
    inputs: [
      {
        name: 'height_feet',
        placeholder: 'Feet',
        isRequired: true,
        inputMask: wholeNumberMask,
      },
      {
        name: 'height_inches',
        placeholder: 'Inches',
        isRequired: true,
        inputMask: wholeNumberMask,
      },
    ],
  }),

  inputPage({
    name: 'weight',
    category: 'Sleep Apnea',
    title: 'What is your weight?',
    description: 'An estimate is okay',
    inputs: [
      {
        name: 'weight_pounds',
        placeholder: 'Weight in pounds',
        isRequired: true,
        inputMask: wholeNumberMask,
      },
    ],
  }),

  // Silent OSA scoring — computes/persists osaRisk for the clinician, no user-facing
  // "sleep apnea screener" result (RET-2554). Auto-advances on mount.
  interstitial({
    name: 'apnea_risk',
    category: 'Sleepiness',
    component: () => <ApneaScoringInterstitial />,
  }),

  radioPage({
    name: 'insomnia_care_preference',
    category: 'Sleep Profile',
    title: 'How would you prefer to get expert sleep help?',
    description: 'We’ll match you to the format that fits your life',
    choices: [
      'Video call visits with a sleep expert',
      'App-only — fully digital program',
      'Not sure — recommend what’s best for me',
    ],
    autoAdvance: true,
    isRequired: true,
  }),

  radioPage({
    name: 'insurance_coverage',
    category: 'Sleep Profile',
    title: 'Check if your insurance unlocks extra support',
    description:
      'Based on your insomnia risk, your benefits may cover a personalized plan with expert sleep help. Most patients pay $0* through eligible insurance.',
    choices: getInsuranceOptionsForState(),
    autoAdvance: true,
    isRequired: true,
    identifyPropertyName: 'insurance_coverage',
  }),

  // Collected for planning only — no Medicare plan is on either payer allowlist
  // yet, so these patients take the same not-covered path as "Other". The split
  // matters once we bill Medicare: CBT-I visits are Part B, so "Part A only" has
  // no outpatient coverage, and Advantage plans are billed to the private carrier.
  radioPage({
    visibleIf: "{insurance_coverage} == 'medicare'",
    name: 'medicare_plan_type',
    category: 'Sleep Profile',
    title: 'Which kind of Medicare do you have?',
    description: 'Your Medicare card or plan card will say. It’s okay if you’re not sure.',
    choices: MEDICARE_PLAN_TYPES,
    autoAdvance: true,
    isRequired: true,
    identifyPropertyName: 'medicare_plan_type',
  }),
];
